<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="450px"
  >
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Employee Separations"
      text="Update employee separations"
      class="mb-0"
    > 
      <form>
        <v-container v-if="dialog">
          <v-row>
            <v-col
              cols="12"
              class="pb-0"
            >
              <v-autocomplete
                v-model="separationType" 
                v-validate="'required'"
                :error-messages="errors.collect('separationType')"
                data-vv-name="separationType"        
                data-vv-as="separation type"
                label="Separation Type"
                :items="separationTypes"   
                item-text="name"
                item-value="id"
                hide-no-data
                outlined
                hide-selected
              />
            </v-col>

            <v-col
              cols="12"
              class="pb-0"
            >
              <v-menu
                v-model="lastWorkingDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="lastWorkingDateDisplay"
                    v-validate="'required'"
                    :error-messages="errors.collect('lastWorkingDateDisplay')"
                    data-vv-name="lastWorkingDateDisplay"
                    :class="{
                      'required':!lastWorkingDateDisplay
                    }"
                    class="label-position"
                    data-vv-as="last working date" 
                    outlined
                    readonly
                    label="Last Working Date"
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template> 
                <v-date-picker
                  v-model="lastWorkingDate"
                  :min="separationMinDate"
                  @input="lastWorkingDateMenu = false"
                />
              </v-menu>
            </v-col>

            <v-col
              cols="12"
              class="pb-0"
            >
              <v-menu
                v-model="effectiveDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="effectiveDateDisplay"
                    v-validate="'required'"
                    label="Effective Date"
                    outlined
                    readonly
                    :error-messages="errors.collect('effectiveDateDisplay')"
                    data-vv-name="effectiveDateDisplay"
                    :class="{
                      'required':!effectiveDateDisplay
                    }"
                    data-vv-as="effective date"
                    class="label-position"
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field> 
                </template>
                <v-date-picker
                  v-model="effectiveDate"
                  :min="separationMinDate"
                  @input="effectiveDateMenu = false"
                />
              </v-menu>
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="reason"
                v-validate="'required'"
                :items="filteredSeparationReasons"
                label="Separation Reason"
                :error-messages="errors.collect('reason')"
                data-vv-name="reason" 
                data-vv-as="separation reason" 
                outlined
                item-text="reason"
                item-value="id"
              />
            </v-col>          
          </v-row>
        </v-container>
      </form>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          class="px-10 white--text"
          color="hrissecondary"          
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      employeeUid: {
        type: String, 
        required: true,
      },
      separationMinDate: {
        type: String, 
        required: true,
      },
    },
    data () {
      return {
        progressing: false,
        dialog: false,
        reason: '',
        separationId: null,
        separationType: null,
        separationTypes: [],
        effectiveDate: null,
        lastWorkingDate: null,
        separationReasons: [],
        lastWorkingDateMenu: false,
        effectiveDateMenu: false,
      }
    },
    computed: {
      filteredSeparationReasons () {
        if (!this.separationType) return []

        return this.separationReasons.filter(separationReason => {
          return separationReason.separation_type_id === this.separationType
        })
      },
      lastWorkingDateDisplay () {
        return this.lastWorkingDate ? this.$moment(this.lastWorkingDate, 'YYYY-MM-DD').format('DD MMM YYYY') : ''
      },
      effectiveDateDisplay () {
        return this.effectiveDate ? this.$moment(this.effectiveDate, 'YYYY-MM-DD').format('DD MMM YYYY') : ''
      },
    },
    watch: {
      dialog: function (val) {
        Promise.all([
          this.$api.hrisSeparationTypes().list(),
          this.$api.separationReasons().list(),
        ]).then(([separationTypes, separationReasons]) => {
          this.separationTypes = separationTypes.data
          this.separationReasons = separationReasons.data

          this.loading = false
        })
      },
    },
    methods: {      
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.hrisEmployeeSeparations().update(this.separationId, this.prepareFormData())
              .then(response => {
                if (response.status >= 200 && response.status < 300) {
                  this.$emit('updated')
                  this.$snack({ message: response.data.status })

                  this.progressing = false
                  this.dialog = false
                }
              })
          })
      },
      toggleDialog (separation) {
        this.reason = separation.separation_reason_id
        this.separationType = separation.separation_type_id
        this.separationId = separation.id

        this.lastWorkingDate = separation.last_working_date
        this.effectiveDate = separation.effective_date

        this.dialog = true
      },
      prepareFormData () {
        return {
          separation_reason_id: this.reason,
          separation_type_id: this.separationType,
          effective_date: this.effectiveDate,
          last_working_date: this.lastWorkingDate,
        }
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:4px
}
</style>
